import { ContextConsumer } from "@lit/context";
import { html, LitElement, TemplateResult } from "lit";
import {
  createDisposable,
  DisposableGroup,
  DisposableLike,
} from "../events/messaging/index";
import { ViewportContext, viewportContext } from "./ViewportContext";

/**
 * Base class for declarative viewport capabilities.
 *
 * Activation happens once per connected context. Context replacement and DOM
 * disconnection always dispose owned resources in reverse registration order.
 */
export abstract class ViewportCapabilityElement extends LitElement {
  private readonly contextConsumer = new ContextConsumer(this, {
    context: viewportContext,
    subscribe: true,
    callback: (value) => this.receiveContext(value),
  });

  private hostConnected = false;
  private currentContext?: ViewportContext;
  private activeContext?: ViewportContext;
  private activeScope?: DisposableGroup;

  protected get viewport(): ViewportContext | undefined {
    return this.currentContext;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.hostConnected = true;
    this.activate();
  }

  override disconnectedCallback(): void {
    this.hostConnected = false;
    try {
      this.deactivate();
    } finally {
      super.disconnectedCallback();
    }
  }

  protected onViewportConnected(
    _context: ViewportContext,
  ): DisposableLike | void {}

  protected onViewportDisconnected(_context: ViewportContext): void {}

  protected own<T extends DisposableLike>(resource: T): T {
    if (!this.activeScope) {
      throw new Error(
        "Viewport capability resources can only be owned while active",
      );
    }
    return this.activeScope.add(resource);
  }

  protected override render(): TemplateResult {
    return html``;
  }

  private receiveContext(value: ViewportContext | undefined): void {
    if (this.currentContext === value) return;
    this.deactivate();
    this.currentContext = value;
    this.activate();
    this.requestUpdate();
  }

  private activate(): void {
    const context = this.currentContext;
    if (
      !this.hostConnected ||
      !context ||
      context.disposed ||
      this.activeContext === context
    ) {
      return;
    }

    const scope = new DisposableGroup();
    this.activeContext = context;
    this.activeScope = scope;
    scope.add(
      createDisposable(() =>
        context.signal.removeEventListener("abort", this.onContextAbort),
      ),
    );
    context.signal.addEventListener("abort", this.onContextAbort, {
      once: true,
    });

    try {
      const cleanup = this.onViewportConnected(context);
      if (cleanup) scope.add(cleanup);
    } catch (error) {
      this.activeContext = undefined;
      this.activeScope = undefined;
      try {
        scope.dispose();
      } catch {
        // Preserve the activation error as the primary failure.
      }
      throw error;
    }
  }

  private deactivate(): void {
    const context = this.activeContext;
    const scope = this.activeScope;
    if (!context || !scope) return;

    this.activeContext = undefined;
    this.activeScope = undefined;
    const errors: unknown[] = [];
    try {
      this.onViewportDisconnected(context);
    } catch (error) {
      errors.push(error);
    }
    try {
      scope.dispose();
    } catch (error) {
      errors.push(error);
    }

    if (errors.length === 1) throw errors[0];
    if (errors.length > 1) {
      throw new AggregateError(errors, "Viewport capability cleanup failed");
    }
  }

  private readonly onContextAbort = () => {
    this.deactivate();
  };
}
